import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Components } from './styled';
import ProjectTextField from '../../../../inputs/ProjectTextField';
import { POLL_MAX_OPTIONS, POLL_MIN_OPTIONS, PollStats } from '../../../../../api/types/lesson';
import { getPollStatsByLessonId } from '../../../../../api/lesson';
import { PollValidationErrors } from './utils';

const AddPoll = ({
  lessonId,
  question,
  setQuestion,
  options,
  setOptions,
  lockedOptionsCount,
  errors,
}: {
  lessonId?: string;
  question: string;
  setQuestion: Dispatch<SetStateAction<string>>;
  options: string[];
  setOptions: Dispatch<SetStateAction<string[]>>;
  // Number of options already stored on the lesson doc. Votes are recorded by index,
  // so these positions must keep their meaning - only appending past them is safe.
  lockedOptionsCount: number;
  errors?: PollValidationErrors;
}) => {
  const [pollStats, setPollStats] = useState<PollStats | undefined>();
  const [areLockedOptionsUnlocked, setAreLockedOptionsUnlocked] = useState(false);

  useEffect(() => {
    if (!lessonId) {
      return;
    }
    getPollStatsByLessonId(lessonId).then(setPollStats);
  }, [lessonId]);

  const isLocked = (index: number) => index < lockedOptionsCount && !areLockedOptionsUnlocked;

  const onChangeOption = (index: number, value: string) => {
    setOptions((oldOptions) => oldOptions.map((option, key) => (key === index ? value : option)));
  };

  const onAddOption = () => {
    setOptions((oldOptions) => (oldOptions.length < POLL_MAX_OPTIONS ? oldOptions.concat(['']) : oldOptions));
  };

  const onRemoveOption = (index: number) => {
    setOptions((oldOptions) =>
      oldOptions.length > POLL_MIN_OPTIONS ? oldOptions.filter((_, key) => key !== index) : oldOptions,
    );
  };

  const onMoveOption = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    setOptions((oldOptions) => {
      if (targetIndex < 0 || targetIndex >= oldOptions.length) {
        return oldOptions;
      }
      const newOptions = [...oldOptions];
      [newOptions[index], newOptions[targetIndex]] = [newOptions[targetIndex], newOptions[index]];
      return newOptions;
    });
  };

  const hasLockedOptions = lockedOptionsCount > 0;
  const canRemove = options.length > POLL_MIN_OPTIONS;
  const canAdd = options.length < POLL_MAX_OPTIONS;

  return (
    <Components.Container>
      {hasLockedOptions && (
        <Components.WarningBanner>
          <Components.WarningTitle>Existing options are locked</Components.WarningTitle>
          <Components.WarningText>
            {`The first ${lockedOptionsCount} option${lockedOptionsCount === 1 ? '' : 's'} ${
              lockedOptionsCount === 1 ? 'has' : 'have'
            } already been published. Responses are stored by position, so editing, reordering or removing them will
            invalidate existing answers. Adding new options at the end is safe.`}
          </Components.WarningText>
          {!areLockedOptionsUnlocked && (
            <Components.UnlockButton onClick={() => setAreLockedOptionsUnlocked(true)}>
              Edit anyway (may invalidate existing responses)
            </Components.UnlockButton>
          )}
        </Components.WarningBanner>
      )}

      <Components.Container>
        <ProjectTextField
          value={question}
          onChangeText={(event) => setQuestion(event.target.value)}
          placeholder={'Enter the poll question'}
          errorMessage={errors?.question}
        />
      </Components.Container>

      {options.map((option, index) => (
        <Components.OptionRow key={index}>
          <Components.OptionIndex>{index + 1}.</Components.OptionIndex>
          <ProjectTextField
            value={option}
            onChangeText={(event) => onChangeOption(index, event.target.value)}
            placeholder={'Enter an option'}
            disabled={isLocked(index)}
          />
          <Components.ReorderButtonsWrapper>
            <Components.SmallButton
              type={'button'}
              onClick={() => onMoveOption(index, -1)}
              disabled={index === 0 || isLocked(index) || isLocked(index - 1)}
              title={'Move up'}>
              ↑
            </Components.SmallButton>
            <Components.SmallButton
              type={'button'}
              onClick={() => onMoveOption(index, 1)}
              disabled={index === options.length - 1 || isLocked(index) || isLocked(index + 1)}
              title={'Move down'}>
              ↓
            </Components.SmallButton>
            <Components.RemoveButton
              type={'button'}
              onClick={() => onRemoveOption(index)}
              disabled={!canRemove || isLocked(index)}
              title={'Remove option'}>
              ✕
            </Components.RemoveButton>
          </Components.ReorderButtonsWrapper>
        </Components.OptionRow>
      ))}

      {errors?.options && <Components.ErrorMessage>{errors.options}</Components.ErrorMessage>}

      <Components.AddButton type={'button'} onClick={onAddOption} disabled={!canAdd}>
        Add option
      </Components.AddButton>
      <Components.HelperText>
        {`Optional - leave the question and all options blank to save the lesson without a poll. If you add one, it needs a question and between ${POLL_MIN_OPTIONS} and ${POLL_MAX_OPTIONS} options. Keep them short - the app renders them as a stacked bar list.`}
      </Components.HelperText>

      {pollStats && pollStats.total > 0 && (
        <Components.ResultsContainer>
          <Components.ResultsTitle>{`Results (${pollStats.total} response${
            pollStats.total === 1 ? '' : 's'
          })`}</Components.ResultsTitle>
          {options.map((option, index) => {
            const count = pollStats.counts?.[index] || 0;
            const percentage = pollStats.total > 0 ? Math.round((count / pollStats.total) * 100) : 0;
            return (
              <Components.ResultRow key={index}>
                <Components.ResultLabelRow>
                  <Components.ResultLabel>{option || `Option ${index + 1}`}</Components.ResultLabel>
                  <Components.ResultCount>{`${count} (${percentage}%)`}</Components.ResultCount>
                </Components.ResultLabelRow>
                <Components.ResultBarTrack>
                  <Components.ResultBarFill percentage={percentage} />
                </Components.ResultBarTrack>
              </Components.ResultRow>
            );
          })}
        </Components.ResultsContainer>
      )}
    </Components.Container>
  );
};

export default AddPoll;
